import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { createIntegrationConnection, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { linearFormSchema, type LinearForm } from "./integrationFormSchemas";

export function LinearConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const formSchema = linearFormSchema.superRefine((values, ctx) => {
    if (!connection && values.apiKey.trim().length < 16) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["apiKey"], message: "API key must contain at least 16 characters." });
    }
  });
  const { register, handleSubmit, formState: { errors } } = useForm<LinearForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: connection?.name ?? "Linear",
      apiKey: "",
      teamId: connection?.configSummary.teamId ?? "",
      createStateId: connection?.configSummary.createStateId ?? "",
    },
  });

  const submit = handleSubmit((values) => action.run("save", async () => {
    const config = {
      teamId: values.teamId,
      ...(values.createStateId ? { createStateId: values.createStateId } : {}),
      ...(values.apiKey ? { apiKey: values.apiKey } : {}),
    };
    try {
      if (connection) await updateIntegrationConnection(connection.id, { name: values.name, config });
      else await createIntegrationConnection({ provider: "linear", name: values.name, config: { ...config, apiKey: values.apiKey }, mappings: {} });
      toast.success(connection ? "Linear settings updated." : "Linear connected.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect Linear.");
    }
  }));

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        Use a dedicated API key where possible. IO Spoke encrypts it and never returns it.
      </div>
      <FormField id="linear-name" label="Connection name" registration={register("name")} error={errors.name} />
      <FormField id="linear-team" label="Team ID" registration={register("teamId")} error={errors.teamId} />
      <FormField id="linear-state" label="Create state ID (optional)" registration={register("createStateId")} error={errors.createStateId} />
      <FormField
        id="linear-key"
        label={`API key ${connection ? "(leave blank to keep current)" : ""}`}
        registration={register("apiKey")}
        error={errors.apiKey}
        type="password"
        autoComplete="new-password"
      />
      <div className="flex gap-2">
        <Button type="submit" disabled={isSubmitting} loading={action.saving} loadingLabel={"Saving…"}>
          {connection ? "Save settings" : "Connect Linear"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
